import AnimatedScore from '@/components/AnimatedScore/AnimatedScore'
import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { useTheme } from '@/contexts/ThemeContext'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import { POSITION_CLASSES, POSITION_ANIMATIONS } from '@/utils/scorebugPosition'
import './BaseballBug.css'

import type { JsonMatchData, BaseballScore, BaseballEventState, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface BaseballBugProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
  scorebugPosition?: ScoreBugPosition
}

const MULTIPLIERS = {
  teamSize: 0,
  scoreBoxSize: 2,
  rheBoxSize: 1.5,
  scoreFontSize: 1.1,
  statFontSize: 0.8,
  columnsFontSize: 0.5,
  columnsLagSize: 0.3,
  indicatorColumnSize: 0.8,
  baseSize: 0.55,
  diamondSize: 1.5,
  inningFontSize: 0.8,
  outsFontSize: 0.8,
}

const BaseballBug: FC<BaseballBugProps> = ({ jsonData, isVisible = true, scorebugPosition = 'bottom-left' }) => {
  const theme = useTheme()
  const { in: animationType } = POSITION_ANIMATIONS[scorebugPosition]
  const animationRef = useTemplateAnimation(isVisible, {
    animationType,
    duration: 0.5,
  })

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Baseball-specific score data
  const homeScore = homeCompetitor?.score as BaseballScore | undefined
  const awayScore = awayCompetitor?.score as BaseballScore | undefined

  const homeRuns = homeScore?.runs ?? 0
  const awayRuns = awayScore?.runs ?? 0
  const homeHits = homeScore?.hits ?? 0
  const awayHits = awayScore?.hits ?? 0
  const homeErrors = homeScore?.errors ?? 0
  const awayErrors = awayScore?.errors ?? 0

  // Event state (shared game state)
  const eventState = jsonData?.eventState as BaseballEventState | undefined
  const inning = eventState?.inning ?? 1
  const inningState = eventState?.inningState ?? 'TOP'
  const outs = eventState?.outs ?? 0
  const bases = eventState?.bases ?? [false, false, false]

  // Layout mode: TOP/BOT = in-innings, MID/END/isFinal = end-innings
  const isFinal = eventState?.isFinal ?? false
  const isInInnings = !isFinal && (inningState === 'TOP' || inningState === 'BOT')

  // Batting: TOP = away batting, BOT = home batting
  const awayIsBatting = inningState === 'TOP'
  const homeIsBatting = inningState === 'BOT'

  // Inning display label
  const inningLabel = isFinal ? 'FINAL' : `${inningState} ${inning}`

  const renderDiamond = () => {
    const diamondSize = `calc(${theme.baseHeight} * ${MULTIPLIERS.diamondSize})`
    const baseSize = `calc(${theme.baseHeight} * ${MULTIPLIERS.baseSize})`
    const halfBase = `calc(${theme.baseHeight} * ${MULTIPLIERS.baseSize / 2})`

    return (
      <div
        className="baseball-bug-diamond"
        style={{ width: diamondSize, height: diamondSize, marginBottom: `calc(${theme.baseHeight} * -0.2)` }}
      >
        {/* 2nd base - top center */}
        <div
          className={`baseball-bug-base${bases[1] ? ' baseball-bug-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: 0,
            left: `calc(50% - ${halfBase})`,
          }}
        />
        {/* 3rd base - middle left */}
        <div
          className={`baseball-bug-base${bases[2] ? ' baseball-bug-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: `calc(50% - ${halfBase})`,
            left: 0,
          }}
        />
        {/* 1st base - middle right */}
        <div
          className={`baseball-bug-base${bases[0] ? ' baseball-bug-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: `calc(50% - ${halfBase})`,
            right: 0,
          }}
        />
      </div>
    )
  }

  // In-innings: right columns = Runs + batting indicator + state panel
  // State panel width = diamond + padding
  const statePanelColumnsSize = MULTIPLIERS.diamondSize + 1.2

  const positionClass = POSITION_CLASSES[scorebugPosition]

  // ---------- IN-INNINGS LAYOUT (TOP / BOT) ----------
  if (isInInnings) {
    return (
      <div className={`baseball-bug-content-wrapper ${positionClass}`}>
        <div ref={animationRef} className="baseball-bug-panel" data-scorebug-panel>
          {/* Header row with inning label */}
          <div className="baseball-bug-header">
            <Trapezoid
              variant="left"
              style={{
                background: hexToRgba(theme.colors.primary, 0.95),
                width: `calc(${theme.baseHeight} * ${statePanelColumnsSize})`,
                height: `calc(${theme.baseHeight} * 0.7)`,
                justifyContent: 'center',
              }}
            >
              <span
                className="baseball-bug-inning-label"
                style={{
                  fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.inningFontSize})`,
                }}
              >
                {inningLabel}
              </span>
            </Trapezoid>
          </div>

          {/* Team rows */}
          <div
            className="baseball-bug-teams"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
            }}
          >
            <div className="baseball-bug-body">
              {/* Teams + scores on left */}
              <div className="baseball-bug-team-rows">
                {/* Away team row (always first) */}
                <div className="baseball-bug-team-row">
                  <Team data={awayCompetitor} style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
                  <div className="baseball-bug-scores-column">
                    <div
                      className="baseball-bug-score-box"
                      style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
                    >
                      <AnimatedScore
                        value={awayRuns}
                        className="baseball-bug-score-value"
                        style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                      />
                    </div>
                  </div>
                  <div
                    className="baseball-bug-batting-column"
                    style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.indicatorColumnSize})` }}
                  >
                    {awayIsBatting && <div className="baseball-bug-batting-indicator" />}
                  </div>
                </div>

                {/* Home team row (always second) */}
                <div className="baseball-bug-team-row">
                  <Team data={homeCompetitor} isHome style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
                  <div className="baseball-bug-scores-column">
                    <div
                      className="baseball-bug-score-box"
                      style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
                    >
                      <AnimatedScore
                        value={homeRuns}
                        className="baseball-bug-score-value"
                        style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                      />
                    </div>
                  </div>
                  <div
                    className="baseball-bug-batting-column"
                    style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.indicatorColumnSize})` }}
                  >
                    {homeIsBatting && <div className="baseball-bug-batting-indicator" />}
                  </div>
                </div>
              </div>

              {/* State panel: diamond + outs (spans both team rows) */}
              <div
                className="baseball-bug-state-panel"
                style={{
                  width: `calc(${theme.baseHeight} * ${statePanelColumnsSize})`,
                }}
              >
                {renderDiamond()}
                <span
                  className="baseball-bug-outs-label"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.outsFontSize})` }}
                >
                  {outs} OUT
                </span>
              </div>
            </div>
          </div>

          {/* Footer bar */}
          <div
            className="baseball-bug-footer"
            style={{
              background: hexToRgba(theme.colors.secondary, 0.95),
              height: `calc(${theme.baseHeight} * 0.8)`,
            }}
          >
            <img
              src="/assets/frogbox-logo-mono.png"
              alt="FrogBox"
              className="baseball-bug-branding-logo"
              style={{ height: `calc(${theme.baseHeight} * 0.4)` }}
            />
          </div>
        </div>
      </div>
    )
  }

  // ---------- END-INNINGS LAYOUT (MID / END) ----------
  return (
    <div className={`baseball-bug-content-wrapper ${positionClass}`}>
      <div ref={animationRef} className="baseball-bug-panel" data-scorebug-panel>
        {/* Header row: thin line + MID 3 trapezoid + transparent gap + R/H/E */}
        <div className="baseball-bug-header" style={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
          {/* Thin decorative line extending from left edge */}
          <div
            style={{
              flex: 1,
              height: `calc(${theme.baseHeight} * 0.06)`,
              background: hexToRgba(theme.colors.primary, 0.95),
              marginRight: `calc(${theme.baseHeight} * -0.1)`,
            }}
          />

          {/* MID 3 inning label */}
          <Trapezoid
            variant="parallelogram"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
              height: `calc(${theme.baseHeight} * 0.7)`,
              paddingLeft: `calc(${theme.baseHeight} * 1)`,
              paddingRight: `calc(${theme.baseHeight} * 1)`,
              justifyContent: 'center',
            }}
          >
            <span
              className="baseball-bug-inning-label"
              style={{
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.inningFontSize})`,
              }}
            >
              {inningLabel}
            </span>
          </Trapezoid>

          {/* R/H/E column headers */}
          <Trapezoid
            variant="left"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
              height: `calc(${theme.baseHeight} * 0.7)`,
              paddingRight: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsLagSize})`,
              marginLeft: `calc(${theme.baseHeight} * -0.4)`,
              justifyContent: 'center',
            }}
          >
            <span
              className="baseball-bug-column-title"
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})`,
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsFontSize})`,
              }}
            >
              R
            </span>
            <span
              className="baseball-bug-column-title"
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})`,
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsFontSize})`,
              }}
            >
              H
            </span>
            <span
              className="baseball-bug-column-title"
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})`,
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsFontSize})`,
              }}
            >
              E
            </span>
          </Trapezoid>
        </div>

        {/* Team rows with R / H / E */}
        <div
          className="baseball-bug-teams"
          style={{
            paddingRight: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsLagSize})`,
            background: hexToRgba(theme.colors.primary, 0.95),
          }}
        >
          {/* Away team row (always first) */}
          <div className="baseball-bug-team-row">
            <Team data={awayCompetitor} style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            <div className="baseball-bug-scores-column" style={{ marginLeft: `calc(${theme.baseHeight} * 0.5)` }}>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={awayRuns}
                  className="baseball-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={awayHits}
                  className="baseball-bug-score-secondary"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.statFontSize})` }}
                />
              </div>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={awayErrors}
                  className="baseball-bug-score-secondary"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.statFontSize})` }}
                />
              </div>
            </div>
          </div>

          {/* Home team row (always second) */}
          <div className="baseball-bug-team-row">
            <Team data={homeCompetitor} isHome style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            <div className="baseball-bug-scores-column" style={{ marginLeft: `calc(${theme.baseHeight} * 0.5)` }}>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={homeRuns}
                  className="baseball-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={homeHits}
                  className="baseball-bug-score-secondary"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.statFontSize})` }}
                />
              </div>
              <div
                className="baseball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.rheBoxSize})` }}
              >
                <AnimatedScore
                  value={homeErrors}
                  className="baseball-bug-score-secondary"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.statFontSize})` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer bar */}
        <div
          className="baseball-bug-footer"
          style={{
            background: hexToRgba(theme.colors.secondary, 0.95),
            height: `calc(${theme.baseHeight} * 0.8)`,
          }}
        >
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            className="baseball-bug-branding-logo"
            style={{ height: `calc(${theme.baseHeight} * 0.4)` }}
          />
        </div>
      </div>
    </div>
  )
}

export default BaseballBug
